import React, { useEffect, useState } from 'react';
import {
  AiChatSettingsInterface,
  AiChatSettingsResponseInterfaceSingle,
  listOfNumberInputs,
} from '../../../../../common/interfaces/aiChatSettings/aiChatSettings.interface';
import { AiChatSettingsService, i18n, NavigationService } from '../../../../../common';
import { Button, Divider, Form, Input, Select, Spin, Tooltip, Radio } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { AdminRoutes } from '../../../_router/admin.routes';
import { useLocation } from 'react-router-dom';

function AiChatSettingsEditPageComponent() {
  const location = useLocation();
  const path = location.pathname.split('/');
  const id = parseInt(path[path.length - 1]);
  const isNew = isNaN(id);

  const [aiChatSettings, setAiChatSettings] = useState<AiChatSettingsResponseInterfaceSingle | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const [limits, setLimits] = useState<{
    premium: 'unlimited' | 'limited' | 'disabled';
    standard: 'unlimited' | 'limited' | 'disabled';
    free: 'unlimited' | 'limited' | 'disabled';
  }>({
    premium: 'disabled',
    standard: 'disabled',
    free: 'disabled',
  });

  useEffect(() => {
    if (isNaN(id)) return;
    AiChatSettingsService.getSingle(id).subscribe((response) => {
      setAiChatSettings(response);
      const getLimitStatus = (limit: number) => {
        if (limit === 0) return 'disabled';
        if (limit > 0) return 'limited';
        return 'unlimited';
      };
      setLimits({
        premium: getLimitStatus(response.data.attributes.monthlyQuestionLimitPremium),
        standard: getLimitStatus(response.data.attributes.monthlyQuestionLimitStandard),
        free: getLimitStatus(response.data.attributes.monthlyQuestionLimitFree),
      });
    });
  }, [id]);

  const onFinishAiChatSettings = (values: any) => {
    setIsLoading(true);
    const request = isNew ? AiChatSettingsService.create : AiChatSettingsService.update;
    const attributes = {
      ...values,
      ...listOfNumberInputs.reduce((acc: any, key: string) => {
        // Handle optional fields (maxTokens and maxCompletionTokens)
        if ((key === 'maxTokens' || key === 'maxCompletionTokens') && (!values[key] || values[key] === '')) {
          // Don't include empty optional fields in the request
          return acc;
        }
        acc[key] = Number(values[key]);
        return acc;
      }, {}),

      azureCosmosdbAccount: '-',
      azureCosmosdbDatabase: '-',
      azureCosmosdbConversationsContainer: '-',
      cosmosdbPrimaryKey: '-',
      cosmosdbConnectionString: '-',
    };

    const getLimitValue = (limit: 'premium' | 'standard' | 'free', value: number) => {
      return limits[limit] === 'limited' ? value : limits[limit] === 'unlimited' ? null : 0;
    }

    attributes.monthlyQuestionLimitPremium = getLimitValue('premium', values.monthlyQuestionLimitPremium);
    attributes.monthlyQuestionLimitStandard = getLimitValue('standard', values.monthlyQuestionLimitStandard);
    attributes.monthlyQuestionLimitFree = getLimitValue('free', values.monthlyQuestionLimitFree);
    
    const body: AiChatSettingsInterface = {
      id: aiChatSettings?.data.id,
      type: 'ai_chat_settings',
      attributes,
    };
    request(body).subscribe(() => {
      setIsLoading(false);
      NavigationService.navigate(AdminRoutes.AI_CHAT_SETTINGS.fullPath);
      setTimeout(() => {
        window.location.reload();
      }, 0);
    });
  };

  const onFinishAiChatSettingsFailed = (errorInfo: any) => {
    console.log('Failed:', errorInfo);
  };

  if (!aiChatSettings && !isNew)
    return (
      <div className="ai-chat-settings-page w100-h100 center-center">
        <Spin size="large" />
      </div>
    );

  const FormInputLimit = (inputName: string, label: string, type: 'premium' | 'standard' | 'free') => {
    return (
      <Form.Item key={inputName} label={<span className="text-bold">{label}</span>}>
        <div className="limit-radio-group-container">
          <Radio.Group
            value={limits[type]}
            onChange={(e) => {
              setLimits({
                ...limits,
                [type]: e.target.value,
              });
            }}
            buttonStyle="solid"
          >
            <Radio.Button value="disabled">Isključeno</Radio.Button>
            <Radio.Button value="limited">Ograničeno</Radio.Button>
            <Radio.Button value="unlimited">Neograničeno</Radio.Button>
          </Radio.Group>
          {limits[type] === 'limited' && (
            <Form.Item
              name={inputName}
              rules={[{ required: true, message: i18n.translate(`genericFormMsg.required`) }]}
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 12 }}
              style={{ width: '100%', marginBottom: 0 }}
            >
              <Input type="number" placeholder="250" style={{ borderRadius: 0 }} />
            </Form.Item>
          )}
        </div>
      </Form.Item>
    );
  };

  return (
    <div className="ai-chat-settings-page w100-h100">
      {/* <div className="title">{i18n.translate('settings.title')}</div> */}
      <div className="action-container">
        <div className="title">AI Chat postavke - {isNew ? 'Kreacija' : 'Uređivanje'}</div>
      </div>

      <Form
        layout="vertical"
        name="aiChatSettings"
        size="large"
        scrollToFirstError
        initialValues={aiChatSettings?.data.attributes}
        onFinish={onFinishAiChatSettings}
        onFinishFailed={onFinishAiChatSettingsFailed}
        autoComplete="off"
        requiredMark={false}
      >
        <Form.Item
          label={<span className="text-bold">Ime modela</span>}
          name="modelName"
          rules={[{ required: true, message: i18n.translate(`genericFormMsg.required`) }]}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 12 }}
        >
          <Input placeholder="Biltenko Azure AI Chat" style={{ borderRadius: 0 }} />
        </Form.Item>

        <Form.Item
          label={<span className="text-bold">Verzija modela</span>}
          name="modelVersion"
          rules={[{ required: true, message: i18n.translate(`genericFormMsg.required`) }]}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 12 }}
        >
          <Input placeholder="v1.0.0" style={{ borderRadius: 0 }} />
        </Form.Item>

        <Form.Item
          label={<span className="text-bold">Opis</span>}
          name="description"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
        >
          <Input.TextArea
            placeholder="Optional description of this chat settings"
            style={{ borderRadius: 0 }}
            rows={4}
          />
        </Form.Item>
        
        <Form.Item
          label={<span className="text-bold">Tip implementacije</span>}
          name="implementationType"
          rules={[{ required: true, message: i18n.translate(`genericFormMsg.required`) }]}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 12 }}
        >
          <Select placeholder="Odaberi tip implementacije" style={{ borderRadius: 0 }}>
            <Select.Option value="AZURE_ON_YOUR_DATA">AZURE ON YOUR DATA</Select.Option>
            <Select.Option value="CLASSIC_RAG">CLASSIC RAG</Select.Option>
          </Select>
        </Form.Item>

        <Divider />

        <Form.Item
          label={<span className="text-bold">Maksimalni broj znakova po poruci</span>}
          name="characterLimit"
          rules={[{ required: true, message: i18n.translate(`genericFormMsg.required`) }]}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 12 }}
        >
          <Input type="number" placeholder="250" style={{ borderRadius: 0 }} />
        </Form.Item>

        {FormInputLimit('monthlyQuestionLimitPremium', 'Maksimalni broj pitanja mjesečno (PREMIUM)', 'premium')}
        {FormInputLimit('monthlyQuestionLimitStandard', 'Maksimalni broj pitanja mjesečno (OSNOVNI)', 'standard')}
        {FormInputLimit('monthlyQuestionLimitFree', 'Maksimalni broj pitanja mjesečno (POČETNI)', 'free')}

        <Divider />

        <Form.Item
          label={<span className="text-bold">Azure Search Strictness</span>}
          name="azureSearchStrictness"
          rules={[{ required: true, message: i18n.translate(`genericFormMsg.required`) }]}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 12 }}
        >
          <Input type="number" placeholder="1" style={{ borderRadius: 0 }} />
        </Form.Item>

        <Form.Item
          label={<span className="text-bold">Azure Search Top K</span>}
          name="azureSearchTopK"
          rules={[{ required: true, message: i18n.translate(`genericFormMsg.required`) }]}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 12 }}
        >
          <Input type="number" placeholder="20" style={{ borderRadius: 0 }} />
        </Form.Item>

        <Form.Item
          label={<span className="text-bold">Max Tokens</span>}
          name="maxTokens"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 12 }}
        >
          <Input type="number" placeholder="4096" style={{ borderRadius: 0 }} />
        </Form.Item>

        <Form.Item
          label={<span className="text-bold">Max Completion Tokens</span>}
          name="maxCompletionTokens"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 12 }}
        >
          <Input type="number" placeholder="4096" style={{ borderRadius: 0 }} />
        </Form.Item>

        <Form.Item
          label={<span className="text-bold">Temperature</span>}
          name="temperature"
          rules={[{ required: true, message: i18n.translate(`genericFormMsg.required`) }]}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 12 }}
        >
          <Input type="number" step="0.1" placeholder="0.2" style={{ borderRadius: 0 }} />
        </Form.Item>

        <Form.Item
          label={<span className="text-bold">Top P</span>}
          name="topP"
          rules={[{ required: true, message: i18n.translate(`genericFormMsg.required`) }]}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 12 }}
        >
          <Input type="number" step="0.1" placeholder="1.0" style={{ borderRadius: 0 }} />
        </Form.Item>

        <Form.Item
          label={<span className="text-bold">Frequency Penalty</span>}
          name="frequencyPenalty"
          rules={[{ required: true, message: i18n.translate(`genericFormMsg.required`) }]}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 12 }}
        >
          <Input type="number" step="0.1" placeholder="0.0" style={{ borderRadius: 0 }} />
        </Form.Item>

        <Form.Item
          label={<span className="text-bold">Presence Penalty</span>}
          name="presencePenalty"
          rules={[{ required: true, message: i18n.translate(`genericFormMsg.required`) }]}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 12 }}
        >
          <Input type="number" step="0.1" placeholder="0.0" style={{ borderRadius: 0 }} />
        </Form.Item>

        <Form.Item
          label={<span className="text-bold">Past Messages</span>}
          name="pastMessages"
          rules={[{ required: true, message: i18n.translate(`genericFormMsg.required`) }]}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 12 }}
        >
          <Input type="number" placeholder="20" style={{ borderRadius: 0 }} />
        </Form.Item>

        <Divider />

        <Form.Item
          label={<span className="text-bold">System Message</span>}
          name="systemMessage"
          rules={[{ required: true, message: i18n.translate(`genericFormMsg.required`) }]}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
        >
          <Input.TextArea placeholder="System message for the chat" style={{ borderRadius: 0 }} rows={10} />
        </Form.Item>

        <Divider />

        <Form.Item
          label={<span className="text-bold">Query Type</span>}
          name="queryType"
          rules={[{ required: true, message: i18n.translate(`genericFormMsg.required`) }]}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 12 }}
        >
          <Select placeholder="Select query type" style={{ borderRadius: 0 }}>
            <Select.Option value="simple">Simple</Select.Option>
            <Select.Option value="semantic">Semantic</Select.Option>
            <Select.Option value="vector">Vector</Select.Option>
            <Select.Option value="vector_simple_hybrid">Vector Simple Hybrid</Select.Option>
            <Select.Option value="vector_semantic_hybrid">Vector Semantic Hybrid</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          label={<span className="text-bold">Semantic Configuration</span>}
          name="semanticConfiguration"
          rules={[{ required: true, message: i18n.translate(`genericFormMsg.required`) }]}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 12 }}
        >
          <Input placeholder="azureml-default" style={{ borderRadius: 0 }} />
        </Form.Item>

        <Form.Item
          label={<span className="text-bold">Strictness</span>}
          name="strictness"
          rules={[{ required: true, message: i18n.translate(`genericFormMsg.required`) }]}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 12 }}
        >
          <Input type="number" placeholder="1" style={{ borderRadius: 0 }} />
        </Form.Item>

        <Form.Item
          label={<span className="text-bold">Top N Documents</span>}
          name="topNDocuments"
          rules={[{ required: true, message: i18n.translate(`genericFormMsg.required`) }]}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 12 }}
        >
          <Input type="number" placeholder="20" style={{ borderRadius: 0 }} />
        </Form.Item>

        <Form.Item
          label={<span className="text-bold">In Scope</span>}
          name="inScope"
          rules={[{ required: true, message: i18n.translate(`genericFormMsg.required`) }]}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 12 }}
        >
          <Select placeholder="Select in scope" style={{ borderRadius: 0 }}>
            <Select.Option value={true}>True</Select.Option>
            <Select.Option value={false}>False</Select.Option>
          </Select>
        </Form.Item>

        <Divider />

        <h2>Fields mapping</h2>

        <Form.Item
          label={
            <Tooltip title="Ako vrijednost sadrži više atributa, razdvojite ih zarezom.">
              <span className="text-bold">Content Fields</span>&nbsp;
              <InfoCircleOutlined />
            </Tooltip>
          }
          name="contentFields"
          rules={[{ required: true, message: i18n.translate(`genericFormMsg.required`) }]}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 12 }}
        >
          <Input placeholder="content" style={{ borderRadius: 0 }} />
        </Form.Item>

        <Form.Item
          label={<span className="text-bold">Filepath Field</span>}
          name="filepathField"
          rules={[{ required: true, message: i18n.translate(`genericFormMsg.required`) }]}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 12 }}
        >
          <Input placeholder="filepath" style={{ borderRadius: 0 }} />
        </Form.Item>

        <Form.Item
          label={<span className="text-bold">Title Field</span>}
          name="titleField"
          rules={[{ required: true, message: i18n.translate(`genericFormMsg.required`) }]}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 12 }}
        >
          <Input placeholder="title" style={{ borderRadius: 0 }} />
        </Form.Item>

        <Form.Item
          label={<span className="text-bold">URL Field</span>}
          name="urlField"
          rules={[{ required: true, message: i18n.translate(`genericFormMsg.required`) }]}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 12 }}
        >
          <Input placeholder="url" style={{ borderRadius: 0 }} />
        </Form.Item>

        <Form.Item
          label={
            <Tooltip title="Ako vrijednost sadrži više atributa, razdvojite ih zarezom.">
              <span className="text-bold">Vector Fields</span>&nbsp;
              <InfoCircleOutlined />
            </Tooltip>
          }
          name="vectorFields"
          rules={[{ required: true, message: i18n.translate(`genericFormMsg.required`) }]}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 12 }}
        >
          <Input placeholder="contentVector" style={{ borderRadius: 0 }} />
        </Form.Item>

        <Divider />

        <Form.Item
          label={<span className="text-bold">Embedding Model Name</span>}
          name="embeddingModelName"
          rules={[{ required: true, message: i18n.translate(`genericFormMsg.required`) }]}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 12 }}
        >
          <Input placeholder="text-embedding-3-large" style={{ borderRadius: 0 }} />
        </Form.Item>

        <Divider />

        <Form.Item
          label={<span className="text-bold">Azure OpenAI Endpoint</span>}
          name="azureOpenaiEndpoint"
          rules={[{ required: true, message: i18n.translate(`genericFormMsg.required`) }]}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 12 }}
        >
          <Input placeholder="endpoint" style={{ borderRadius: 0 }} />
        </Form.Item>

        <Form.Item
          label={<span className="text-bold">Azure OpenAI Deployment ID</span>}
          name="azureOpenaiDeploymentId"
          rules={[{ required: true, message: i18n.translate(`genericFormMsg.required`) }]}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 12 }}
        >
          <Input placeholder="deploymentId" style={{ borderRadius: 0 }} />
        </Form.Item>

        <Form.Item
          label={<span className="text-bold">Azure Search Key</span>}
          name="azureSearchKey"
          rules={[{ required: true, message: i18n.translate(`genericFormMsg.required`) }]}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 12 }}
        >
          <Input placeholder="azureSearchKey" style={{ borderRadius: 0 }} />
        </Form.Item>

        <Form.Item
          label={<span className="text-bold">Azure AI Search Endpoint</span>}
          name="azureAiSearchEndpoint"
          rules={[{ required: true, message: i18n.translate(`genericFormMsg.required`) }]}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 12 }}
        >
          <Input placeholder="azureAiSearchEndpoint" style={{ borderRadius: 0 }} />
        </Form.Item>

        <Form.Item
          label={<span className="text-bold">Azure AI Search Index</span>}
          name="azureAiSearchIndex"
          rules={[{ required: true, message: i18n.translate(`genericFormMsg.required`) }]}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 12 }}
        >
          <Input placeholder="gpt-4o" style={{ borderRadius: 0 }} />
        </Form.Item>

        <Form.Item
          label={<span className="text-bold">Azure OpenAI Key</span>}
          name="azureOpenaiKey"
          rules={[{ required: true, message: i18n.translate(`genericFormMsg.required`) }]}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 12 }}
        >
          <Input placeholder="azureOpenaiKey" style={{ borderRadius: 0 }} />
        </Form.Item>

        {/* <Form.Item
          label={<span className="text-bold">Azure Cosmos DB Account</span>}
          name="azureCosmosdbAccount"
          rules={[{ required: true, message: i18n.translate(`genericFormMsg.required`) }]}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 12 }}
        >
          <Input placeholder="azureCosmosdbAccount" style={{ borderRadius: 0 }} />
        </Form.Item>

        <Form.Item
          label={<span className="text-bold">Azure Cosmos DB Database</span>}
          name="azureCosmosdbDatabase"
          rules={[{ required: true, message: i18n.translate(`genericFormMsg.required`) }]}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 12 }}
        >
          <Input placeholder="azureCosmosdbDatabase" style={{ borderRadius: 0 }} />
        </Form.Item>

        <Form.Item
          label={<span className="text-bold">Azure Cosmos DB Conversations Container</span>}
          name="azureCosmosdbConversationsContainer"
          rules={[{ required: true, message: i18n.translate(`genericFormMsg.required`) }]}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 12 }}
        >
          <Input placeholder="azureCosmosdbConversationsContainer" style={{ borderRadius: 0 }} />
        </Form.Item>

        <Form.Item
          label={<span className="text-bold">Cosmos DB Primary Key</span>}
          name="cosmosdbPrimaryKey"
          rules={[{ required: true, message: i18n.translate(`genericFormMsg.required`) }]}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 12 }}
        >
          <Input placeholder="cosmosdbPrimaryKey" style={{ borderRadius: 0 }} />
        </Form.Item>

        <Form.Item
          label={<span className="text-bold">Cosmos DB Connection String</span>}
          name="cosmosdbConnectionString"
          rules={[{ required: true, message: i18n.translate(`genericFormMsg.required`) }]}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 12 }}
        >
          <Input placeholder="cosmosdbConnectionString" style={{ borderRadius: 0 }} />
        </Form.Item> */}

        <Divider />

        <div>
          <Button
            className="button"
            htmlType="submit"
            type="primary"
            size="large"
            style={{ fontSize: 16, fontWeight: 700, borderRadius: 0, minWidth: 193, marginBottom: 24 }}
            loading={isLoading}
          >
            {i18n.translate('genericButtons.save')}
          </Button>
        </div>
      </Form>
    </div>
  );
}

export default AiChatSettingsEditPageComponent;
